import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import { formatDate, cn } from '@/lib/utils.ts'
import { useAnonymize } from '@/hooks/useAnonymize.ts'
import {
  ArrowLeft, CheckCircle, XCircle, Loader2, Package,
  Receipt, Building, FileText, Edit2, Save,
} from 'lucide-react'
import { DataTable, ErrorState } from '@/components/ui'
import type { InvoiceWithLines, InvoiceStatus } from '@shared/types'

const STATUS_LABELS: Record<InvoiceStatus, string> = {
  pending: 'Ausstehend',
  extracted: 'Erkannt',
  matched: 'Zugeordnet',
  approved: 'Freigegeben',
  booked: 'In SAP gebucht',
  rejected: 'Abgelehnt',
  error: 'Fehler',
}

const STATUS_COLORS: Record<InvoiceStatus, string> = {
  pending: 'bg-gray-100 text-gray-700',
  extracted: 'bg-blue-100 text-blue-700',
  matched: 'bg-indigo-100 text-indigo-700',
  approved: 'bg-yellow-100 text-yellow-700',
  booked: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  error: 'bg-red-100 text-red-800',
}

export function InvoiceDetailPage() {
  const a = useAnonymize()
  const formatCurrency = (x: number) => a.fmtCurrency(x)
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [editForm, setEditForm] = useState<Record<string, string>>({})
  const [rejectReason, setRejectReason] = useState('')
  const [showReject, setShowReject] = useState(false)

  const { data: invoice, isLoading, isError, error } = useQuery<InvoiceWithLines>({
    queryKey: ['invoices', id],
    queryFn: () => apiClient.get(`/invoices/${id}`).then((r) => r.data),
    enabled: !!id,
  })

  const { data: suppliers } = useQuery({
    queryKey: ['invoices', 'sap', 'suppliers'],
    queryFn: () => apiClient.get('/invoices/sap/suppliers').then((r) => r.data?.data ?? []),
  })

  const approve = useMutation({
    mutationFn: () => apiClient.post(`/invoices/${id}/approve`).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
  })

  const reject = useMutation({
    mutationFn: () => apiClient.post(`/invoices/${id}/reject`, { reason: rejectReason }).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      setShowReject(false)
    },
  })

  const goodsReceipt = useMutation({
    mutationFn: () => apiClient.post(`/invoices/${id}/goods-receipt`).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
  })

  const updateInvoice = useMutation({
    mutationFn: (updates: Record<string, unknown>) => apiClient.patch(`/invoices/${id}`, updates).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      setEditing(false)
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    )
  }

  if (isError) {
    return (
      <ErrorState
        title="Rechnung konnte nicht geladen werden"
        description={(error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Bitte erneut versuchen.'}
        details={error ? JSON.stringify(error, Object.getOwnPropertyNames(error), 2) : undefined}
        onRetry={() => navigate('/rechnungen')}
        retryLabel="Zur Übersicht"
      />
    )
  }

  if (!invoice) {
    return (
      <ErrorState
        title="Rechnung nicht gefunden"
        description="Diese Rechnung existiert nicht oder wurde gelöscht."
        onRetry={() => navigate('/rechnungen')}
        retryLabel="Zur Übersicht"
      />
    )
  }

  const canApprove = ['extracted', 'matched'].includes(invoice.status) && invoice.supplierCardCode
  const canReject = ['pending', 'extracted', 'matched'].includes(invoice.status)
  const canEdit = !['booked'].includes(invoice.status)
  const canGoodsReceipt = invoice.status === 'booked' && !invoice.sapGoodsReceiptEntry

  const startEditing = () => {
    setEditing(true)
    setEditForm({
      supplierName: invoice.supplierName ?? '',
      supplierCardCode: invoice.supplierCardCode ?? '',
      invoiceNumber: invoice.invoiceNumber ?? '',
      invoiceDate: invoice.invoiceDate ? new Date(invoice.invoiceDate).toISOString().slice(0, 10) : '',
      dueDate: invoice.dueDate ? new Date(invoice.dueDate).toISOString().slice(0, 10) : '',
      netAmount: invoice.netAmount != null ? String(invoice.netAmount / 100) : '',
      vatAmount: invoice.vatAmount != null ? String(invoice.vatAmount / 100) : '',
      grossAmount: invoice.grossAmount != null ? String(invoice.grossAmount / 100) : '',
      sapPurchaseOrderNum: invoice.sapPurchaseOrderNum ?? '',
    })
  }

  const saveEditing = () => {
    const updates: Record<string, unknown> = {}
    if (editForm.supplierName !== (invoice.supplierName ?? '')) updates.supplierName = editForm.supplierName
    if (editForm.supplierCardCode !== (invoice.supplierCardCode ?? '')) updates.supplierCardCode = editForm.supplierCardCode
    if (editForm.invoiceNumber !== (invoice.invoiceNumber ?? '')) updates.invoiceNumber = editForm.invoiceNumber
    if (editForm.invoiceDate) updates.invoiceDate = editForm.invoiceDate
    if (editForm.dueDate) updates.dueDate = editForm.dueDate
    if (editForm.netAmount) updates.netAmount = Math.round(parseFloat(editForm.netAmount) * 100)
    if (editForm.vatAmount) updates.vatAmount = Math.round(parseFloat(editForm.vatAmount) * 100)
    if (editForm.grossAmount) updates.grossAmount = Math.round(parseFloat(editForm.grossAmount) * 100)
    if (editForm.sapPurchaseOrderNum !== (invoice.sapPurchaseOrderNum ?? ''))
      updates.sapPurchaseOrderNum = editForm.sapPurchaseOrderNum

    updateInvoice.mutate(updates)
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/rechnungen')} className="p-1 rounded-lg hover:bg-gray-100">
            <ArrowLeft className="w-5 h-5 text-gray-500" />
          </button>
          <Receipt className="w-6 h-6 text-brand-500" />
          <h1 className="text-2xl font-bold text-gray-800">
            Rechnung {invoice.invoiceNumber ? a.docNum(invoice.invoiceNumber) : '#' + invoice.id.slice(0, 8)}
          </h1>
          <span className={cn('px-2.5 py-1 rounded-full text-xs font-medium', STATUS_COLORS[invoice.status])}>
            {STATUS_LABELS[invoice.status]}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && !editing && (
            <button onClick={startEditing} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
              <Edit2 className="w-4 h-4" />
              Bearbeiten
            </button>
          )}
          {editing && (
            <>
              <button onClick={() => setEditing(false)} className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
                Abbrechen
              </button>
              <button onClick={saveEditing} disabled={updateInvoice.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-brand-500 text-white rounded-lg hover:bg-brand-600 disabled:opacity-50">
                {updateInvoice.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Speichern
              </button>
            </>
          )}
          {canReject && (
            <button onClick={() => setShowReject(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-red-300 text-red-600 rounded-lg hover:bg-red-50">
              <XCircle className="w-4 h-4" />
              Ablehnen
            </button>
          )}
          {canApprove && (
            <button onClick={() => approve.mutate()} disabled={approve.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
              {approve.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              Freigeben & Buchen
            </button>
          )}
          {canGoodsReceipt && (
            <button onClick={() => goodsReceipt.mutate()} disabled={goodsReceipt.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
              {goodsReceipt.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4" />}
              Wareneingang buchen
            </button>
          )}
        </div>
      </div>

      {/* Error Messages */}
      {approve.isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
          Fehler beim Buchen: {(approve.error as Error).message}
        </div>
      )}
      {invoice.errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
          {invoice.errorMessage}
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-2 gap-4">
        {/* Left: Invoice Details */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <h2 className="font-semibold text-gray-800 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Rechnungsdaten
          </h2>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Lieferant" editing={editing}
              value={invoice.supplierName ? a.name(invoice.supplierName) : '-'}
              editValue={editForm.supplierName}
              onChange={(v) => setEditForm((f) => ({ ...f, supplierName: v }))} />

            <div>
              <p className="text-xs text-gray-400 mb-0.5">SAP-Lieferant</p>
              {editing ? (
                <select
                  value={editForm.supplierCardCode}
                  onChange={(e) => setEditForm((f) => ({ ...f, supplierCardCode: e.target.value }))}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
                >
                  <option value="">-- Auswahl --</option>
                  {(suppliers as Array<{ CardCode: string; CardName: string }> ?? []).map((s) => (
                    <option key={s.CardCode} value={s.CardCode}>{a.name(s.CardName)} ({a.code(s.CardCode)})</option>
                  ))}
                </select>
              ) : (
                <p className="text-sm font-medium text-gray-800">
                  {invoice.supplierCardCode ? a.code(invoice.supplierCardCode) : <span className="text-orange-500">Nicht zugeordnet</span>}
                </p>
              )}
            </div>

            <Field label="Rechnungsnr." editing={editing}
              value={invoice.invoiceNumber ? a.docNum(invoice.invoiceNumber) : '-'}
              editValue={editForm.invoiceNumber}
              onChange={(v) => setEditForm((f) => ({ ...f, invoiceNumber: v }))} />

            <Field label="Rechnungsdatum" editing={editing} type="date"
              value={invoice.invoiceDate ? formatDate(invoice.invoiceDate) : '-'}
              editValue={editForm.invoiceDate}
              onChange={(v) => setEditForm((f) => ({ ...f, invoiceDate: v }))} />

            <Field label="Faelligkeitsdatum" editing={editing} type="date"
              value={invoice.dueDate ? formatDate(invoice.dueDate) : '-'}
              editValue={editForm.dueDate}
              onChange={(v) => setEditForm((f) => ({ ...f, dueDate: v }))} />

            <Field label="Bestellnummer" editing={editing}
              value={invoice.sapPurchaseOrderNum ? a.docNum(invoice.sapPurchaseOrderNum) : '-'}
              editValue={editForm.sapPurchaseOrderNum}
              onChange={(v) => setEditForm((f) => ({ ...f, sapPurchaseOrderNum: v }))} />
          </div>

          {/* Amounts */}
          <div className="border-t border-gray-100 pt-3">
            <h3 className="text-sm font-medium text-gray-600 mb-2">Betraege</h3>
            <div className="grid grid-cols-3 gap-3">
              <AmountField label="Netto" editing={editing}
                value={invoice.netAmount != null ? formatCurrency(invoice.netAmount / 100) : '-'}
                editValue={editForm.netAmount}
                onChange={(v) => setEditForm((f) => ({ ...f, netAmount: v }))} />
              <AmountField label="MwSt." editing={editing}
                value={invoice.vatAmount != null ? formatCurrency(invoice.vatAmount / 100) : '-'}
                editValue={editForm.vatAmount}
                onChange={(v) => setEditForm((f) => ({ ...f, vatAmount: v }))} />
              <AmountField label="Brutto" editing={editing}
                value={invoice.grossAmount != null ? formatCurrency(invoice.grossAmount / 100) : '-'}
                editValue={editForm.grossAmount}
                onChange={(v) => setEditForm((f) => ({ ...f, grossAmount: v }))} />
            </div>
          </div>

          {/* SAP References */}
          {(invoice.sapPurchaseInvoiceEntry || invoice.sapGoodsReceiptEntry) && (
            <div className="border-t border-gray-100 pt-3">
              <h3 className="text-sm font-medium text-gray-600 mb-2">SAP-Referenzen</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {invoice.sapPurchaseInvoiceEntry && (
                  <div>
                    <p className="text-xs text-gray-400">Eingangsrechnung</p>
                    <p className="font-mono text-gray-800">DocEntry: {invoice.sapPurchaseInvoiceEntry}</p>
                  </div>
                )}
                {invoice.sapGoodsReceiptEntry && (
                  <div>
                    <p className="text-xs text-gray-400">Wareneingang</p>
                    <p className="font-mono text-gray-800">DocEntry: {invoice.sapGoodsReceiptEntry}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="border-t border-gray-100 pt-3 text-xs text-gray-400 space-y-1">
            <p>Quelle: {invoice.source === 'email' ? 'E-Mail' : invoice.source === 'portal_amazon' ? 'Amazon' : 'Manueller Upload'}</p>
            <p>Datei: {invoice.originalFileName ?? '-'}</p>
            {invoice.oneDrivePath && <p>OneDrive: {invoice.oneDrivePath}</p>}
            {invoice.extractionConfidence != null && (
              <p>Erkennungsgenauigkeit: {invoice.extractionConfidence}%</p>
            )}
            <p>Erstellt: {formatDate(invoice.createdAt, { hour: '2-digit', minute: '2-digit' })}</p>
          </div>
        </div>

        {/* Right: Line Items */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <h2 className="font-semibold text-gray-800 flex items-center gap-2">
            <Building className="w-4 h-4" />
            Positionen ({invoice.lines?.length ?? 0})
          </h2>

          {invoice.lines && invoice.lines.length > 0 ? (
            <>
              <DataTable
                data={invoice.lines}
                columns={[
                  {
                    accessorKey: 'lineNum',
                    header: '#',
                    cell: ({ row }) => <span className="text-gray-400">{row.original.lineNum}</span>,
                  },
                  {
                    accessorKey: 'description',
                    header: 'Beschreibung',
                    cell: ({ row }) => (
                      <span className="text-gray-700">
                        {row.original.description ? a.freeText(row.original.description) : '-'}
                        {row.original.itemCode && (
                          <span className="ml-1 text-xs text-gray-400 font-mono">({a.code(row.original.itemCode)})</span>
                        )}
                      </span>
                    ),
                  },
                  {
                    accessorKey: 'quantity',
                    header: 'Menge',
                    cell: ({ row }) => (
                      <span className="block text-right text-gray-600 tabular-nums">
                        {(row.original.quantity / 1000).toFixed(row.original.quantity % 1000 ? 2 : 0)}
                      </span>
                    ),
                  },
                  {
                    accessorKey: 'unitPrice',
                    header: 'Einzel',
                    cell: ({ row }) => (
                      <span className="block text-right text-gray-600 tabular-nums">
                        {formatCurrency(row.original.unitPrice / 100)}
                      </span>
                    ),
                  },
                  {
                    accessorKey: 'totalPrice',
                    header: 'Gesamt',
                    cell: ({ row }) => (
                      <span className="block text-right font-medium text-gray-800 tabular-nums">
                        {formatCurrency(row.original.totalPrice / 100)}
                      </span>
                    ),
                  },
                ]}
                getRowId={(line) => String(line.id)}
                enableSorting={false}
              />
              <div className="mt-2 flex items-center justify-end gap-3 pt-2 border-t border-gray-200">
                <span className="font-medium text-gray-600">Summe</span>
                <span className="font-bold text-gray-800 tabular-nums">
                  {formatCurrency(invoice.lines.reduce((s, l) => s + l.totalPrice, 0) / 100)}
                </span>
              </div>
            </>
          ) : (
            <p className="text-gray-400 text-sm">Keine Positionen erkannt</p>
          )}
        </div>
      </div>

      {/* Reject Modal */}
      {showReject && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Rechnung ablehnen</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Grund fuer die Ablehnung (optional)"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none h-24"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowReject(false)} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
                Abbrechen
              </button>
              <button onClick={() => reject.mutate()} disabled={reject.isPending}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                {reject.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                Ablehnen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Helper Components ──────────────────────────────────────────────────────

function Field({ label, value, editing, editValue, onChange, type = 'text' }: {
  label: string; value: string; editing: boolean; editValue?: string; onChange?: (v: string) => void; type?: string
}) {
  return (
    <div>
      <p className="text-xs text-gray-400 mb-0.5">{label}</p>
      {editing && onChange ? (
        <input type={type} value={editValue ?? ''} onChange={(e) => onChange(e.target.value)}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md" />
      ) : (
        <p className="text-sm font-medium text-gray-800">{value}</p>
      )}
    </div>
  )
}

function AmountField({ label, value, editing, editValue, onChange }: {
  label: string; value: string; editing: boolean; editValue?: string; onChange?: (v: string) => void
}) {
  return (
    <div>
      <p className="text-xs text-gray-400 mb-0.5">{label}</p>
      {editing && onChange ? (
        <input type="number" step="0.01" value={editValue ?? ''} onChange={(e) => onChange(e.target.value)}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md text-right" />
      ) : (
        <p className="text-sm font-bold text-gray-800">{value}</p>
      )}
    </div>
  )
}
